import gql from 'graphql-tag';
import {pick} from 'lodash';
import {useCallback, useMemo} from 'react';
import {createSelector, type Selector} from 'reselect';

import {makeRunSetSelector} from '../../components/RunSelector/runSetSelectors';
import * as Generated from '../../generated/graphql';
import {type RootState} from '../../types/redux';
import {useCachedData} from '../../util/cache';
import {propagateErrorsContext} from '../../util/errors';
import type * as FilterTypes from '../../util/filterTypes';
import type * as RunTypes from '../../util/runTypes';
import {type RunSetConfig} from '../../util/section';
import type * as SM from '../../util/selectionmanager';
import {runIdsToNames} from '../../util/selectionmanager';
import {useMemoizedSelector} from '../hooks';
import {useRunsQueryContext} from '../runs/hooks';
import type * as CustomRunNamesTypes from '../views/customRunNames/types';
import {type Ref as FilterRef} from '../views/filter/types';
import * as ViewHooks from '../views/hooks';
import {useRunSet, useRunSetProject} from '../views/runSet/hooks';
import type * as RunSetTypes from '../views/runSet/types';
import {type Ref as RunSetRef, type RunSetPart} from '../views/runSet/types';
import type * as TempSelectionsTypes from '../views/tempSelections/types';
import {combinedFilter} from './runSelectorQuery';

/**
 * Uses createSelector per https://reselect.js.org/api/createSelector
 * Colocated here since it's specific to useSelectedCountsWithRefetch.
 */
const makeRunSetCountQueryPropsSelector = (
  runSetRef: RunSetRef
): Selector<
  RootState,
  {
    filtersRef: FilterRef;
    project: RunSetPart['project'];
    search: RunSetPart['search'];
  }
> =>
  createSelector(makeRunSetSelector(runSetRef), runSet => ({
    filtersRef: runSet.filtersRef as FilterRef,
    project: runSet.project,
    search: runSet.search,
  }));

///// Query definition
export const SELECTED_COUNT_QUERY = gql`
  query SelectedCount(
    $projectInternalId: ID!
    $filters: JSONString
    $tempFilters: JSONString
  ) {
    project(internalId: $projectInternalId) {
      id
      selectedCount: runCount(filters: $filters)
      tempSelectedCount: runCount(filters: $tempFilters)
      tempSelectedActiveCount: runActiveCount(filters: $tempFilters)
      tempOutputArtifactsCount: runOutputArtifactsCount(filters: $tempFilters)
    }
  }
`;

interface QueryInputProps {
  mergeFilters?: FilterTypes.Filter<RunTypes.Key>;
  spamFilters?: FilterTypes.Filter<RunTypes.Key>;
  filters: FilterTypes.Filter<RunTypes.Key>;
  groupSelection: SM.GroupSelectionState;
  groupTempSelection?: SM.GroupSelectionState;
  search?: RunSetConfig['search'];
  customRunNames?: CustomRunNamesTypes.CustomRunNames;
  projectInternalId: string | undefined;
}

export function useSelectedCountQuery(
  inputProps: QueryInputProps
): ReturnType<typeof useSelectedCountQueryNoCache> {
  const selectedCountQuery = useSelectedCountQueryNoCache(inputProps);

  const initialLoading =
    selectedCountQuery.loading && selectedCountQuery.data == null;

  const selectedCountFromServerOrCache = useCachedData({
    namespace: 'SelectedCountQuery',
    key: inputProps,
    dataFromServer: selectedCountQuery.data,
    waitingForDataFromServer: initialLoading,
  });

  return useMemo(() => {
    if (initialLoading && selectedCountFromServerOrCache != null) {
      return {
        ...selectedCountQuery,
        data: selectedCountFromServerOrCache,
        loading: false,
      };
    }
    return selectedCountQuery;
  }, [initialLoading, selectedCountFromServerOrCache, selectedCountQuery]);
}

function useSelectedCountQueryNoCache(inputProps: QueryInputProps) {
  return Generated.useSelectedCountQuery({
    variables: {
      projectInternalId: inputProps.projectInternalId ?? '',
      filters: JSON.stringify(
        combinedFilter(
          inputProps.filters,
          inputProps.groupSelection,
          inputProps.spamFilters,
          inputProps.mergeFilters,
          inputProps.search,
          inputProps.customRunNames
        )
      ),
      tempFilters: JSON.stringify(
        combinedFilter(
          inputProps.filters,
          inputProps.groupTempSelection,
          undefined,
          inputProps.mergeFilters,
          inputProps.search,
          inputProps.customRunNames
        )
      ),
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
    context: propagateErrorsContext(),
    skip: inputProps.projectInternalId == null,
  });
}

/**
 * Get the selection state for a run set, with pinned runs always included. This
 * is useful for visibility counting, since pinned runs are always visible.
 *
 * @param runSetRef
 * @returns SelectionManager GroupSelectionState that includes any pinned runs
 */
function useSelectionsIncludingPins(
  runSetRef: RunSetTypes.Ref
): SM.GroupSelectionState {
  const runSet = useRunSet(runSetRef);

  return useMemo(() => {
    const pinnedRunIds = runSet.pinnedRunIds ?? [];

    // N.B. `[1].concat([])` is a tricksy no-op, so is useful to us here when
    //      baselineRunId is not set.
    const allPinnedRuns = pinnedRunIds.concat(runSet.baselineRunId ?? []);
    // Pinned runs are always visible ("selected"), so always include them in
    // the selection state, even if they aren't explicitly selected.
    const selectedPinnedRowNames = runIdsToNames(allPinnedRuns);

    if (runSet.grouping.length) {
      return pick(runSet, ['selections', 'grouping', 'expandedRowAddresses']);
    }
    return {
      selections: {
        ...runSet.selections,
        selectedPinnedRowNames,
      },
      grouping: runSet.grouping,
      expandedRowAddresses: runSet.expandedRowAddresses,
      pinnedRowIds: allPinnedRuns,
    };
  }, [runSet]);
}

type Counts = {
  tempOutputArtifactsCount: number;
  tempSelectedCount: number;
  tempSelectedActiveCount: number;
  selectedCount: number;
};

const zeroCounts: Counts = {
  tempOutputArtifactsCount: 0,
  tempSelectedCount: 0,
  tempSelectedActiveCount: 0,
  selectedCount: 0,
};

/**
 * Get selected counts for a run set. Returns a tuple of two items, the second
 * is a function to refetch the counts. If counts are not loaded, all properties
 * will be zero.
 *
 * @param runSetRef
 * @param tempSelectionsRef
 * @param customRunNamesRef
 * @return Tuple of counts and refetch function.
 */
export function useSelectedCountsWithRefetch(
  runSetRef: RunSetTypes.Ref,
  tempSelectionsRef?: TempSelectionsTypes.Ref,
  customRunNamesRef?: CustomRunNamesTypes.Ref
): readonly [Counts, () => void] {
  const customRunNames = ViewHooks.usePartMaybe(customRunNamesRef);
  const tempSelections = ViewHooks.usePartMaybe(tempSelectionsRef);

  // Use useMemoizedSelector with reselect selector factory.
  // See components/RunSelector/runSetSelectors.ts for available selectors.
  const partialRunSet = useMemoizedSelector(
    makeRunSetCountQueryPropsSelector,
    runSetRef
  );

  const filters = ViewHooks.usePart(partialRunSet.filtersRef);

  const runsQueryContext = useRunsQueryContext();
  const {projectInternalId} = useRunSetProject(partialRunSet.project);
  const groupSelections = useSelectionsIncludingPins(runSetRef);

  const groupTempSelection = tempSelections
    ? {
        grouping: groupSelections.grouping,
        selections: tempSelections,
        expandedRowAddresses: groupSelections.expandedRowAddresses,
      }
    : undefined;

  const selectedCountQueryProps = {
    ...runsQueryContext,
    projectInternalId,
    filters,
    groupSelection: groupSelections,
    groupTempSelection,
    search: partialRunSet.search,
    customRunNames,
  };
  const selectedCountQuery = useSelectedCountQuery(selectedCountQueryProps);

  const refetchCounts = useCallback(() => {
    selectedCountQuery.refetch();
  }, [selectedCountQuery]);

  const counts = useMemo(() => {
    if (!selectedCountQuery.data?.project) {
      return zeroCounts;
    }

    const project = selectedCountQuery.data.project;
    return pick(project, [
      'tempOutputArtifactsCount',
      'tempSelectedCount',
      'tempSelectedActiveCount',
      'selectedCount',
    ]);
  }, [selectedCountQuery.data?.project]);

  return [counts, refetchCounts] as const;
}

/**
 * As with `useSelectedCountsWithRefetch`, but only returns the counts, not the
 * refetch function.
 *
 * @param runSetRef
 * @param tempSelectionsRef
 * @param customRunNamesRef
 * @returns Counts
 */
export function useSelectedCounts(
  runSetRef: RunSetTypes.Ref,
  tempSelectionsRef?: TempSelectionsTypes.Ref,
  customRunNamesRef?: CustomRunNamesTypes.Ref
): Counts {
  const [counts] = useSelectedCountsWithRefetch(
    runSetRef,
    tempSelectionsRef,
    customRunNamesRef
  );
  return counts;
}
